import type { TrackPayload, TrackResponse } from './types'

const RETRY_STORAGE_KEY = '_el_pending'
const MAX_RETRY_ATTEMPTS = 6
const BASE_RETRY_DELAY_MS = 2_000

interface SendOptions {
  apiBaseUrl: string
  publishableKey: string
}

interface QueueItem {
  payload: TrackPayload
  attempts: number
  enqueuedAt: number
}

/**
 * localStorage 큐 항목 shape 검증. localStorage는 다른 탭/확장/사용자 조작으로 오염될 수 있는
 * 신뢰 불가 입력이므로, 산술 연산(`attempts * attempts`)에 쓰기 전 최소 shape를 확인한다.
 */
const isQueueItem = (value: unknown): value is QueueItem => {
  if (value == null || typeof value !== 'object') return false
  if (!('attempts' in value) || !('enqueuedAt' in value) || !('payload' in value)) return false
  return (
    typeof value.attempts === 'number' &&
    typeof value.enqueuedAt === 'number' &&
    value.payload != null &&
    typeof value.payload === 'object'
  )
}

const safeReadQueue = (): QueueItem[] => {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(RETRY_STORAGE_KEY)
    if (raw == null || raw === '') return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isQueueItem) : []
  } catch {
    return []
  }
}

const safeWriteQueue = (items: QueueItem[]): void => {
  if (typeof localStorage === 'undefined') return
  try {
    if (items.length === 0) localStorage.removeItem(RETRY_STORAGE_KEY)
    else localStorage.setItem(RETRY_STORAGE_KEY, JSON.stringify(items))
  } catch {
    // 용량 초과/Safari ITP 등 — silent drop
  }
}

const enqueueRetry = (payload: TrackPayload): void => {
  const queue = safeReadQueue()
  queue.push({ payload, attempts: 0, enqueuedAt: Date.now() })
  safeWriteQueue(queue)
}

/**
 * 키 자체가 거부된 응답(401 인증 실패, 403 플랜 미제공/Origin 불허)인지 판정.
 * 같은 키로 보내는 모든 이벤트가 같은 이유로 거부되므로 큐에 남은 이벤트도 함께 버린다 (#843).
 */
const isKeyRejected = (status: number): boolean => status === 401 || status === 403

/** 재시도해도 의미 없는 응답(429 를 제외한 4xx)인지 판정 */
const isNonRetryableClientError = (status: number): boolean => status >= 400 && status < 500 && status !== 429

/** 서버 오류(5xx), 429 처럼 나중에 다시 보낼 가치가 있는 상태 코드인지 판정 */
const isRetryableStatus = (status: number): boolean => (status >= 500 && status < 600) || status === 429

/** 키가 거부되면 큐를 비운다. 재시도 큐가 같은 키로 6회 백오프를 반복하는 것을 막는다 */
const dropQueueIfKeyRejected = (status: number): void => {
  if (isKeyRejected(status)) safeWriteQueue([])
}

/**
 * fetch keepalive로 전환 이벤트를 전송.
 *
 * sendBeacon은 Authorization 헤더 설정 제약이 있어 본 SDK는 fetch keepalive로 통일한다.
 * publishableKey는 클라이언트 노출이 전제이므로 Authorization 헤더 전달이 안전.
 *
 * - 2xx: 성공
 * - 4xx (429 제외): 재시도 무의미 → silent drop. 401/403 은 키 자체가 거부된 것이므로 대기 큐도 비운다
 * - 5xx / network / 429: enqueueRetry로 큐잉 → 지수 백오프 재시도 (최대 6회 / 24시간)
 *
 * 본 함수는 fire-and-forget. 호출 측은 응답을 기다리지 않는다.
 */
const sendKeepaliveFetch = (url: string, payload: TrackPayload, key: string): boolean => {
  if (typeof fetch === 'undefined') return false
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  })
    .then(response => {
      if (response.ok) return
      if (isNonRetryableClientError(response.status)) {
        dropQueueIfKeyRejected(response.status)
        return
      }
      enqueueRetry(payload)
    })
    .catch(() => enqueueRetry(payload))
  return true
}

const sendTrack = (payload: TrackPayload, options: SendOptions): void => {
  sendKeepaliveFetch(`${options.apiBaseUrl}/api/v1/track`, payload, options.publishableKey)
}

/**
 * localStorage 큐에 쌓인 실패 이벤트를 재시도. SDK init 시 1회 호출.
 * 지수 백오프: attempts^2 * BASE_RETRY_DELAY_MS 이후만 재시도 대상.
 */
const drainRetryQueue = async (options: SendOptions): Promise<void> => {
  const queue = safeReadQueue()
  if (queue.length === 0) return
  const now = Date.now()
  const remaining: QueueItem[] = []
  for (const item of queue) {
    const delay = Math.min(item.attempts * item.attempts * BASE_RETRY_DELAY_MS, 5 * 60 * 1000)
    if (item.attempts >= MAX_RETRY_ATTEMPTS) continue
    if (now - item.enqueuedAt < delay) {
      remaining.push(item)
      continue
    }
    const response = await fetch(`${options.apiBaseUrl}/api/v1/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${options.publishableKey}`,
      },
      body: JSON.stringify(item.payload),
      credentials: 'omit',
    }).catch(() => null)
    if (response == null || isRetryableStatus(response.status)) {
      remaining.push({ ...item, attempts: item.attempts + 1 })
      continue
    }
    if (isKeyRejected(response.status)) {
      // 같은 키의 나머지 이벤트도 같은 이유로 거부되므로 더 보내지 않고 큐를 비운다
      safeWriteQueue([])
      return
    }
  }
  safeWriteQueue(remaining)
}

export { sendTrack, drainRetryQueue, type TrackResponse }
